import React, { createContext, useCallback, useEffect, useMemo, useRef, useState, useContext } from "react";
import * as NotificationAPI from '../services/NotificationServices';
import AuthContext from "./AuthContext";
import socket, { addJoinEvent, removeJoinEvent } from '../socket';

const NotificationContext = createContext();

const TOAST_DURATION_MS = 5000;
const BADGE_STORAGE_PREFIX = 'notificationBadgeDismissed:';

// The bell badge is dismissed by id, not by a timestamp: ids come from the
// server, so nothing here depends on the client clock being in sync.
const readDismissedIds = (userId) => {
    if (!userId) return [];
    try {
        const parsed = JSON.parse(localStorage.getItem(`${BADGE_STORAGE_PREFIX}${userId}`));
        return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
        return [];
    }
};

const writeDismissedIds = (userId, ids) => {
    if (!userId) return;
    try {
        localStorage.setItem(`${BADGE_STORAGE_PREFIX}${userId}`, JSON.stringify(ids));
    } catch (error) {
        // Storage unavailable (private mode / quota): the dismissal still holds
        // for this session, it just won't survive a reload.
    }
};

/**
 * App-level live notification state. Mounted above the router so notifications
 * keep flowing on pages that don't render a navbar (auth pages, /create/*).
 *
 * Also owns the personal-room 'join' registration — other features (feedback,
 * admin messaging) target io.to(userId), so joining here makes their events
 * deliverable app-wide instead of only where a navbar happened to be mounted.
 */
function NotificationContextProvider(props) {
    const { userId, user } = useContext(AuthContext);

    const [notifications, setNotifications] = useState([]);
    // Bell-click UX: opening the dropdown clears the "!" badge without marking
    // items seen (blue dots stay). A new arrival re-lights it.
    const [badgeDismissed, setBadgeDismissed] = useState(false);
    // Ids of the unseen notifications that were on screen at the last dismissal.
    // Persisted per user so the badge stays down across reloads while anything
    // that arrived *after* the dismissal still lights it.
    const [dismissedIds, setDismissedIds] = useState(() => new Set());
    const [toasts, setToasts] = useState([]);

    const toastIdRef = useRef(0);
    const seededUserIdRef = useRef(null);
    // First 'connect' after login rides on the fresh auth seed; every later
    // 'connect' is a reconnect and needs a refetch to recover missed pushes.
    const everConnectedRef = useRef(false);

    const dismissToast = useCallback((toastId) => {
        setToasts((prev) => prev.filter((t) => t.id !== toastId));
    }, []);

    const pushToast = useCallback((notification) => {
        // Counter id, not _id: announcement/assignment pushes arrive without one.
        const id = `toast-${++toastIdRef.current}`;
        setToasts((prev) => [...prev, { id, notification }]);
        setTimeout(() => dismissToast(id), TOAST_DURATION_MS);
    }, [dismissToast]);

    const refetchNotifications = useCallback(async () => {
        try {
            const data = await NotificationAPI.getNotifications();
            if (Array.isArray(data?.notifications)) {
                // Full replace: the endpoint is authoritative (every push is
                // persisted before it's emitted), and this heals live items
                // that arrived without an _id.
                setNotifications(data.notifications);
                return data.notifications;
            }
        } catch (error) {
            console.error('Error refetching notifications:', error);
        }
        return null;
    }, []);

    // Seed from the login snapshot; clear everything on logout. The badge only
    // resets when the account actually changes — re-seeds from getLoggedIn()
    // (profile edits, tier changes) must not resurrect a dismissed badge.
    useEffect(() => {
        if (user?.notifications) {
            setNotifications(user.notifications);
            if (seededUserIdRef.current !== user._id) {
                seededUserIdRef.current = user._id;
                setBadgeDismissed(false);
            }
        } else if (!user) {
            seededUserIdRef.current = null;
            setNotifications([]);
            setBadgeDismissed(false);
            setToasts([]);
        }
    }, [user]);

    // Load (or clear) the persisted dismissal for whoever is signed in.
    useEffect(() => {
        setDismissedIds(new Set(readDismissedIds(userId)));
    }, [userId]);

    useEffect(() => {
        if (!userId) return;

        const handleNotification = (notification) => {
            setNotifications((prev) =>
                notification._id && prev.some((n) => n._id === notification._id)
                    ? prev // dedupe: a refetch may already have delivered it
                    : [notification, ...prev]
            );
            setBadgeDismissed(false);
            pushToast(notification);
        };

        const handleConnect = () => {
            if (everConnectedRef.current) {
                refetchNotifications();
            } else {
                everConnectedRef.current = true;
            }
        };

        // If the socket is already connected when we subscribe, the next
        // 'connect' event is by definition a reconnect.
        everConnectedRef.current = socket.connected;

        // `forUserId` so this never goes out on a socket opened before sign-in.
        // This effect runs BEFORE AuthContext's — React runs child effects first —
        // so at this moment the socket module still thinks the session is the
        // logged-out one it resolved earlier.
        addJoinEvent('join', userId, undefined, { forUserId: userId });
        socket.on('notification', handleNotification);
        socket.on('connect', handleConnect);

        return () => {
            removeJoinEvent('join');
            socket.off('notification', handleNotification);
            socket.off('connect', handleConnect);
        };
    }, [userId, pushToast, refetchNotifications]);

    const unseenCount = useMemo(
        () => notifications.filter((n) => !n.seen).length,
        [notifications]
    );

    // Lit only for unseen items the user hasn't already been shown a badge for.
    const hasNewNotification = useMemo(
        () => !badgeDismissed && notifications.some((n) => !n.seen && !(n._id && dismissedIds.has(n._id))),
        [badgeDismissed, notifications, dismissedIds]
    );

    const dismissBadge = useCallback(async () => {
        setBadgeDismissed(true);

        let current = notifications;
        if (current.some((n) => !n.seen && !n._id)) {
            // Announcement/assignment pushes arrive without an _id, so they
            // can't be recorded as dismissed. Refetch to heal them into real
            // subdocs first, otherwise the badge returns on the next reload.
            const healed = await refetchNotifications();
            if (healed) current = healed;
        }

        const unseenIds = current.filter((n) => !n.seen && n._id).map((n) => n._id);
        setDismissedIds(new Set(unseenIds));
        writeDismissedIds(userId, unseenIds);
    }, [notifications, refetchNotifications, userId]);

    // Mutations update local state and rethrow so callers keep their own
    // loading/error handling and navigation.
    const markAsSeen = useCallback(async (notificationId) => {
        await NotificationAPI.markNotificationAsSeen(userId, notificationId);
        setNotifications((prev) =>
            prev.map((n) => (n._id === notificationId ? { ...n, seen: true } : n))
        );
    }, [userId]);

    // Per-classroom unread announcement counts, keyed by classroom id as a
    // string — live socket pushes carry relatedClassroomId as a raw string
    // while the persisted copies are ObjectIds.
    const unseenAnnouncementCounts = useMemo(() => {
        const counts = new Map();
        notifications.forEach((n) => {
            if (n.seen || n.notificationType !== 'announcement' || !n.relatedClassroomId) return;
            const key = String(n.relatedClassroomId);
            counts.set(key, (counts.get(key) || 0) + 1);
        });
        return counts;
    }, [notifications]);

    const getUnseenAnnouncementCount = useCallback(
        (classroomId) => (classroomId ? unseenAnnouncementCounts.get(String(classroomId)) || 0 : 0),
        [unseenAnnouncementCounts]
    );

    // Called from effects when a classroom's announcements come into view, so
    // it swallows failures rather than rethrowing — there's no UI to surface
    // them and the count simply stays up until the next attempt.
    const markClassroomAnnouncementsAsSeen = useCallback(async (classroomId) => {
        if (!classroomId) return;
        try {
            const data = await NotificationAPI.markClassroomAnnouncementsAsSeen(classroomId);
            if (Array.isArray(data?.notifications)) {
                setNotifications(data.notifications);
            }
        } catch (error) {
            console.error('Error marking classroom announcements as seen:', error);
        }
    }, []);

    const acceptInvite = useCallback(async (relatedClassroomId, role, notificationId) => {
        await NotificationAPI.acceptInvite(relatedClassroomId, role, notificationId);
        setNotifications((prev) => prev.filter((n) => n._id !== notificationId));
    }, []);

    const ignoreInvite = useCallback(async (notificationId) => {
        await NotificationAPI.ignoreInvite(notificationId);
        setNotifications((prev) => prev.filter((n) => n._id !== notificationId));
    }, []);

    const value = useMemo(() => ({
        notifications,
        unseenCount,
        hasNewNotification,
        dismissBadge,
        markAsSeen,
        getUnseenAnnouncementCount,
        markClassroomAnnouncementsAsSeen,
        acceptInvite,
        ignoreInvite,
        refetchNotifications,
        toasts,
        dismissToast,
    }), [
        notifications,
        unseenCount,
        hasNewNotification,
        dismissBadge,
        markAsSeen,
        getUnseenAnnouncementCount,
        markClassroomAnnouncementsAsSeen,
        acceptInvite,
        ignoreInvite,
        refetchNotifications,
        toasts,
        dismissToast,
    ]);

    return (
        <NotificationContext.Provider value={value}>
            {props.children}
        </NotificationContext.Provider>
    );
}

export default NotificationContext;
export { NotificationContextProvider };
